import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import {
  ADD_CHAT_MESSAGE,
  ADD_DEFAULT_CHAT,
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  SET_CHAT_MESSAGES,
  SET_CHAT_OUTAGE_MESSAGE,
  SET_CURRENT_CHAT_ID,
  SET_UNREAD_MESSAGE_COUNT,
  SET_LAST_UPDATED_AT,
  UPDATE_CHAT,
  UPDATE_CHAT_TITLE,
} from 'actions/actionConstants'
import {
  CREATE_NEW_CHAT,
  FETCH_CHAT_MESSAGES,
  FETCH_CHAT_WELCOME_MESSAGE,
  FETCH_CHATS,
  POST_MESSAGE_FEEDBACK,
  USER_LOGOUT,
} from 'actions/apiActionConstants'
import { DEFAULT_CHAT } from 'supportAssistant/hooks/useChats'
import { omitKeys } from 'lib/object/objectUtils'

export const initialSupportAssistantState: App.SupportAssistantState = {
  chats: { chats: [DEFAULT_CHAT] },
  welcomeMessage: { message: undefined },
  currentChatId: 'default',
  messages: {},
  messagesLoading: {},
  messagesError: {},
  outageMessage: undefined,
  unreadMessageCount: 0,
  lastUpdatedAt: '',
}

const apiCalls = apiReducerSwitch<App.SupportAssistantState>({
  [FETCH_CHATS]: {
    request: () => ({
      chats: {
        chats: [],
        fetching: true,
      },
    }),
    success: (state, action) => ({
      chats: {
        chats: action.data.chats,
        fetching: false,
        error: undefined,
      },
      currentChatId: action.data.currentChatId,
    }),
    failure: (state, action) => ({
      chats: {
        chats: [],
        fetching: false,
        error: action.error?.message ?? action.error,
      },
    }),
  },
  [CREATE_NEW_CHAT]: {
    request: (state) => ({
      chats: {
        chats: [
          ...state.chats.chats,
        ],
        fetching: true,
      },
    }),
    success: (state, action) => {
      // if the new chat created does not have a customerId, the user is logged out
      // So we only want to save the new chat.
      const chats = !action.data.chat.customerId ?
          [action.data.chat] :
          [
            ...state.chats.chats,
            action.data.chat,
          ]

      return {
        chats: {
          chats,
          fetching: false,
          error: undefined,
        },
        messages: {
          ...state.messages,
          [action.data.chat.id]: action.data.messages,
        },
        currentChatId: action.data.chat.id,
      }
    },
    failure: (state, action) => ({
      chats: {
        chats: [
          ...state.chats.chats,
        ],
        fetching: false,
        error: action.error?.message ?? action.error,

      },
    }),
  },
  [FETCH_CHAT_WELCOME_MESSAGE]: {
    request: () => ({
      welcomeMessage: {
        message: undefined,
        fetching: true,
      },
    }),
    success: (state, action) => ({
      welcomeMessage: {
        message: action.data,
        fetching: false,
        error: undefined,
      },
    }),
    failure: (state, action) => ({
      welcomeMessage: {
        message: undefined,
        fetching: true,
        error: action.error?.message ?? action.error,
      },
    }),
  },
  [FETCH_CHAT_MESSAGES]: {
    request: (state, action) => ({
      messages: omitKeys([action.chatId], state.messages),
      messagesLoading: {
        ...state.messagesLoading,
        [action.chatId]: true,
      },
      messagesError: {
        ...state.messagesError,
        [action.chatId]: undefined,
      },
    }),
    success: (state, action) => ({
      messages: {
        ...state.messages,
        [action.chatId]:
          action.data.messages,
      },
      messagesLoading: {
        ...state.messagesLoading,
        [action.chatId]: false,
      },
      messagesError: {
        ...state.messagesError,
        [action.chatId]: undefined,
      },
    }),
    failure: (state, action) => ({
      messages: omitKeys([action.chatId], state.messages),
      messagesLoading: {
        ...state.messagesLoading,
        [action.chatId]: false,
      },
      messagesError: {
        ...state.messagesError,
        [action.chatId]: action.error?.message ?? action.error,
      },
    }),
  },
  [POST_MESSAGE_FEEDBACK]: {
    request: (state, action) => ({
      messagesLoading: {
        ...state.messagesLoading,
        [action.feedback.chatId]: true,
      },
      messagesError: {
        ...state.messagesError,
        [action.feedback.chatId]: undefined,
      },
    }),
    success: (state, action) => {
      const updatedMessages = state.messages[action.data.chatId]?.map((message) => {
        return message.id === action.data.messageId ?
            { ...message, feedback: action.data.sentiment } :
          message
      })
      return {
        messages: {
          ...state.messages,
          [action.data.chatId]: updatedMessages,
        },
        messagesLoading: {
          ...state.messagesLoading,
          [action.data.chatId]: false,
        },
        messagesError: {
          ...state.messagesError,
          [action.data.chatId]: undefined,
        },
      }
    },
    failure: (state, action) => ({
      messagesLoading: {
        ...state.messagesLoading,
        [action.feedback.chatId]: false,
      },
      messagesError: {
        ...state.messagesError,
        [action.feedback.chatId]: action.error?.message ?? action.error,
      },
    }),
  },
  [USER_LOGOUT]: {
    success: () => initialSupportAssistantState,
  },
})

export default createAppReducer<App.SupportAssistantState>(initialSupportAssistantState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [ADD_CHAT_MESSAGE]: (state, action) => ({
    messages: {
      ...state.messages,
      [action.chatId]: [
        ...(state.messages[action.chatId] || []),
        action.message,
      ],
    },
  }),
  [SET_CHAT_MESSAGES]: (state, action) => ({
    messages: {
      ...state.messages,
      [action.chatId]: action.messages,
    },
  }),
  [ADD_DEFAULT_CHAT]: (state, action) => ({
    chats: {
      chats: [action.chat],
      fetching: false,
      error: undefined,
    },
    currentChatId: action.chat.id,
  }),
  [UPDATE_CHAT]: (state, action) => {
    const updatedChats = state.chats.chats.map((chat) => chat.id === action.chat.id ?
      action.chat :
      chat,
    )

    return {
      chats: {
        chats: updatedChats,
        fetching: false,
        error: undefined,
      },
    }
  },
  [UPDATE_CHAT_TITLE]: (state, action) => {
    const updatedChats = state.chats.chats.map((chat) =>
      chat.id === action.chatId ?
          { ...chat, title: action.title } :
        chat,
    )
    return {
      chats: {
        chats: updatedChats,
        fetching: false,
        error: undefined,
      },
    }
  },
  [SET_CURRENT_CHAT_ID]: (state, action) => ({
    currentChatId: action.chatId,
  }),
  [SET_CHAT_OUTAGE_MESSAGE]: (state, action) => ({
    outageMessage: action.error,
  }),
  [SET_UNREAD_MESSAGE_COUNT]: (state, action) => ({
    unreadMessageCount: action.count,
  }),
  [SET_LAST_UPDATED_AT]: (state, action) => ({
    lastUpdatedAt: action.lastUpdatedAt,
  }),
})
